import { Guid } from 'guid-typescript';
import React, { useEffect, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import KOGFileUploader from '@/main/components/shared/fileUploader/fileUploader';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KogTextAreaControlledNew from '@/main/components/shared/textArea/textArea';
import { getBrand, useBrandReducer } from '@/main/store/brand';
import { getPackage, usePackageReducer } from '@/main/store/package';
import { getPartType, usePartTypeReducer } from '@/main/store/partType';
import KogInputDimensionsMaskControlled from '@/main/components/shared/inputText/inputDimensionsMaskControlled';
import KogModalSlideImages from '@/main/components/shared/KogModalSlideImages/modalSlideImages';
import { KeyValue, KeyValueDefault } from '@/main/models/common';
import { SupplierInventoryDataResponse } from '@/main/models/response';
import { PartTypeEnum } from '@/main/services/enum/PartTypeEnum';
import { StatusSupplierInventoryWarehouseEnum } from '@/main/services/enum/StatusSupplierInventoryWarehouseEnum';
import { getDefaultOptionDrop, getTranslateByKey, isEmpty, isNullUndefined } from '@/main/services/utils';

import { IInventoryRegisterForm } from '../utilsInventoryRegisterScreen';
import * as S from './styles';

interface IPartDetailProps {
  dataInventory: SupplierInventoryDataResponse | null;
  watcherCountry: KeyValue | null;
  imagesPart: File[];
  setImagesPart: React.Dispatch<React.SetStateAction<File[]>>
  setImagesDelete: React.Dispatch<React.SetStateAction<string[]>>;
}

function SectionPartDetail({
  imagesPart,
  dataInventory,
  watcherCountry,
  setImagesPart,
  setImagesDelete,
}: IPartDetailProps) {
  const status: StatusSupplierInventoryWarehouseEnum = isNullUndefined(dataInventory) ? 0 : dataInventory.idStatus
  const dispatch = useDispatch();
  const { partType: partTypeReducer } = usePartTypeReducer();
  const { brand: brandReducer } = useBrandReducer();
  const { package: packageReducer } = usePackageReducer();
  const optionDefaultSupplierTypeDrop = getDefaultOptionDrop(
    partTypeReducer.data
  );
  const optionDefaultBrandDrop = getDefaultOptionDrop(brandReducer.data);

  const [imageIndex, setImageIndex] = useState(0);
  const [isOpenModalFade, setIsOpenModalFade] = useState<boolean>(false);
  const [images, setImages] = useState<string[]>([]);

  const {
    control,
    setValue,
    clearErrors,
  } = useFormContext<IInventoryRegisterForm>();
  const watcherType = useWatch({
    name: 'type',
    control,
  })
  const { errors } = useFormState({
    control,
  })

  useEffect(() => {
    dispatch(getPartType());
    dispatch(getBrand());
  }, []);

  useEffect(() => {
    if (!isNullUndefined(watcherCountry)) {
      dispatch(getPackage({ guidCountry: String(watcherCountry.key) }));
    }
  }, [watcherCountry?.key]);

  useEffect(() => {
    if (dataInventory) {
      setValue('sku', dataInventory.sku);
      setValue('name', dataInventory.title);

      if (
        isNullUndefined(dataInventory.guidPartBrand) ||
        isNullUndefined(dataInventory.partBrand) ||
        dataInventory.guidPartBrand === Guid.EMPTY
      ) {
        setValue('partBrand', null);
      } else {
        setValue('partBrand', {
          key: dataInventory.guidPartBrand,
          value: dataInventory.partBrand
        });
      }

      if (!isEmpty(dataInventory.amount)) {
        setValue('amount', dataInventory.amount);
      }
      if (dataInventory.dimensions) {
        const formattedDimensions = dataInventory.dimensions
          .split(' x ')
          .map(dimension => {
            return parseFloat(dimension).toString();
          })
          .join(' x ');

        setValue('dimensions', formattedDimensions);
      }
      setValue('weight', dataInventory.weight);

      if (!isNullUndefined(dataInventory.guidPackageType) && !isNullUndefined(dataInventory.packageContent)) {
        setValue('packageContent', {
          key: dataInventory.guidPackageType,
          value: dataInventory.packageContent
        });
      }

      setValue('warrantyPeriod', dataInventory.warranty);
      setValue('pickupTime', dataInventory.pickupTime);
      setValue('color', dataInventory.color);
      setValue('observation', dataInventory.description);
      if (dataInventory.partNumberOEM) {
        setValue('partNumberOem', dataInventory.partNumberOEM)
      }
      if (dataInventory.partNumberIAM) {
        setValue('partNumberIam', dataInventory.partNumberIAM)
      }
      const initialImages = [
        {
          name: dataInventory.image1,
          type: 'url'
        },
        {
          name: dataInventory.image2,
          type: 'url'
        },
        {
          name: dataInventory.image3,
          type: 'url'
        },
        {
          name: dataInventory.image4,
          type: 'url'
        },
        {
          name: dataInventory.image5,
          type: 'url'
        },
        {
          name: dataInventory.image6,
          type: 'url'
        }
      ];
      setImagesPart(initialImages.filter(item => item.name) as File[]);
    }
  }, [JSON.stringify(dataInventory)]);

  useEffect(() => {
    if (!isNullUndefined(dataInventory?.guidPartType) && !isNullUndefined(partTypeReducer.data)) {
      const findPartType = partTypeReducer.data.find(x => x.key === dataInventory.guidPartType)

      if (findPartType) {
        setValue('type', {
          key: findPartType.key,
          value: findPartType.value,
          default: findPartType.default
        });
      } else {
        setValue('type', null);
      }
    }
  }, [dataInventory?.guidPartType, partTypeReducer.data])

  useEffect(() => {
    if (imagesPart && imagesPart.length > 0) {
      let imagesString: string[] = [];
      imagesPart.map((item) => {
        if (item.type === 'url') {
          return imagesString.push(item.name);
        }
        if (item as File && item.type.startsWith('image')) {
          return imagesString.push(URL.createObjectURL(item as File));
        }
      });
      return setImages(imagesString);
    }
    return setImages([]);
  }, [imagesPart])

  function handleTypeChange(selectedType: KeyValueDefault | null) {
    if (Number(selectedType?.default) !== PartTypeEnum.OEM) {
      clearErrors('partNumberOem');
    }
  };

  function handleClickPreview(index: number, urlFile: string) {
    setImageIndex(index);
    setIsOpenModalFade(true);
  }
  const labelDimensions = `${getTranslateByKey('partdetail.dimensions')}(${getTranslateByKey('information.height')} x ${getTranslateByKey('information.width')} x ${getTranslateByKey('information.depth')})`;

  return (
    <S.Container>
      <S.HeaderSection>
        <S.HeaderText>{getTranslateByKey('quotation.partDetail')}</S.HeaderText>
      </S.HeaderSection>
      <S.RowInput>
        <KogInputTextControlledNew
          label={getTranslateByKey('information.sku')}
          controllerProps={{ rules: { required: true } }}
          name="sku"
          error={!!errors.sku}
          control={control}
          id="id-inventoryRegister-txt-sku"
          errorMessageCustom={
            errors.sku?.type === 'required'
              ? getTranslateByKey('information.skuRequired')
              : ''
          }
        />
        <KogInputTextControlledNew
          label={getTranslateByKey('information.name')}
          controllerProps={{ rules: { required: true } }}
          name="name"
          error={!!errors.name}
          control={control}
          id="id-inventoryRegister-txt-name"
          errorMessageCustom={
            errors.name?.type === 'required'
              ? getTranslateByKey('information.nameRequired')
              : ''
          }
        />
        <KogAutoCompleteControlled
          nameCustom="type"
          label={getTranslateByKey('information.type')}
          control={control}
          rulesCustom={{ required: true }}
          errorMessageCustom={
            errors.type?.type === 'required'
              ? getTranslateByKey('information.typeRequired')
              : ''
          }
          hasErrorCustom={!!errors.type}
          options={partTypeReducer.data}
          isLoading={partTypeReducer.isLoading}
          onChangeCustom={(e) => {
            handleTypeChange(e as KeyValueDefault | null);
          }}
          id="id-inventoryRegister-txt-country"
          autoHighlightCustom={!!optionDefaultSupplierTypeDrop}
          setValueCustom={setValue}
        />
        <KogAutoCompleteControlled
          nameCustom="partBrand"
          label={getTranslateByKey('information.partBrand')}
          control={control}
          rulesCustom={{ required: false }}
          options={brandReducer.data}
          isLoading={brandReducer.isLoading}
          onChangeCustom={() => { }}
          id="id-inventoryRegister-txt-partBrand"
          autoHighlightCustom={!!optionDefaultBrandDrop}
          setValueCustom={setValue}
        />
        <KogInputTextControlledNew
          label={getTranslateByKey('information.partNumberOem')}
          name="partNumberOem"
          error={!!errors.partNumberOem}
          control={control}
          required={Number(watcherType?.default) === PartTypeEnum.OEM}
          id="id-inventoryRegister-txt-partNumberOem"
          errorMessageCustom={
            errors.partNumberOem?.type === 'required'
              ? getTranslateByKey('information.oemPartNumberRequired', true)
              : ''
          }
        />
        <KogInputTextControlledNew
          label={getTranslateByKey("information.iamPartNumber")}
          name="partNumberIam"
          error={!!errors.partNumberIam}
          control={control}
          id="id-inventoryRegister-txt-partNumberIam"
        />
        <KogInputTextControlledNew
          label={getTranslateByKey('information.amount')}
          controllerProps={{ rules: { required: true } }}
          inputProps={{ maxLength: 9 }}
          name="amount"
          hasOnlyNumber
          error={!!errors.amount}
          control={control}
          id="id-inventoryRegister-txt-amount"
          errorMessageCustom={
            errors.amount?.type === 'required'
              ? getTranslateByKey('information.amountRequired')
              : ''
          }
        />
        <KogInputDimensionsMaskControlled
          id="id-inventoryRegister-txt-dimensions"
          name="dimensions"
          control={control}
          label={labelDimensions}
          required
          controllerProps={{ rules: { required: true } }}
        />
        <KogInputTextControlledNew
          label={`${getTranslateByKey('information.weight')} (kg)`}
          controllerProps={{ rules: { required: true } }}
          name="weight"
          hasOnlyNumber
          error={!!errors.weight}
          control={control}
          id="id-inventoryRegister-txt-weight"
          errorMessageCustom={
            errors.weight?.type === 'required'
              ? getTranslateByKey('information.weightRequired')
              : ''
          }
        />
        <KogAutoCompleteControlled
          nameCustom="packageContent"
          label={getTranslateByKey('information.packageType')}
          control={control}
          rulesCustom={{ required: true }}
          hasErrorCustom={!!errors.packageContent}
          options={packageReducer.data}
          isLoading={packageReducer.isLoading}
          onChangeCustom={() => { }}
          id="id-inventoryRegister-txt-packageContent"
          setValueCustom={setValue}
          errorMessageCustom={
            status === StatusSupplierInventoryWarehouseEnum.PendingToMap && !errors.packageContent ? getTranslateByKey('information.labelPackagePendToMap') : ''
          }
        />
        <KogInputTextControlledNew
          label={getTranslateByKey('information.warrantyPeriodDays')}
          name="warrantyPeriod"
          error={!!errors.warrantyPeriod}
          control={control}
          id="id-inventoryRegister-txt-warrantyPeriod"
        />
        <KogInputTextControlledNew
          name="pickupTime"
          required
          label={getTranslateByKey('information.pickupTime')}
          error={!!errors.pickupTime}
          control={control}
          id="id-inventoryRegister-txt-pickupTime"
          hasOnlyNumber
          inputProps={{ maxLength: 5 }}
          controllerProps={{ rules: { required: true } }}
          errorMessageCustom={
            errors.pickupTime?.type === 'required'
              ? getTranslateByKey('information.pickupTimeRequired', true)
              : ''
          }
        />
        <KogInputTextControlledNew
          label={getTranslateByKey('partdetail.color')}
          name="color"
          error={!!errors.color}
          control={control}
          id="id-inventoryRegister-txt-color"
        />
      </S.RowInput>
      <S.ContainerObservation>
        <KogTextAreaControlledNew
          id="id-inventoryRegister-text-observation"
          name="observation"
          required
          control={control}
          errorMessageCustom={
            errors.observation?.type ===
              'required'
              ? getTranslateByKey(
                'information.descriptionRequired', true
              )
              : ''
          }
          rulesCustom={{ required: true }}
          label={getTranslateByKey('information.description')}
        />
      </S.ContainerObservation>
      <S.ContainerDragDrop>
        <KOGFileUploader
          files={imagesPart}
          typeFiles={'image'}
          maxFiles={6}
          setFiles={setImagesPart}
          setImagesDelete={setImagesDelete}
          handleClickPreviewCustom={handleClickPreview}
        />
      </S.ContainerDragDrop>

      {images && images.length > 0 && (
        <KogModalSlideImages
          images={images}
          imageIndex={imageIndex}
          isOpen={isOpenModalFade}
          setIsOpen={setIsOpenModalFade}
        />
      )}
    </S.Container>
  );
}

export default SectionPartDetail;
